// @ts-nocheck
import { Dialog } from '@headlessui/react';
import React, { useRef } from 'react';

import Transition, {
  MODAL_CONTENT_TRANSITION,
  MODAL_OVERLAY_TRANSITION,
  SIDE_MODAL_CONTENT_TRANSITION,
} from '@/components/Transition';

import { MODAL_ZINDEX_MIN } from '../constants';
import type { ModalItem } from '../useModal';
import { DEFAULT_MODAL_CONFIG } from '../useModal';
import _ModalLayer from './_ModalLayer';
import _SideModalLayer from './_SideModalLayer';

export interface ModalProps {
  item?: ModalItem;
  isPeak?: boolean;
  zIndex?: number;
  onClose: () => void;
}

/**
 * 1. Headless UI Dialog(Modal)은 Portal로 구현되어 있음
 * - @see Doc https://headlessui.dev/react/dialog#rendering-to-a-portal
 *
 * 2. 해당 UI의 경우 Headless 측에서 중복 모달에 대한 고려 없이 기본적으로 개발이 되었기 때문에,
 * 다음 몇 가지 트릭을 통해 형제 Level 모달간 이벤트 버블링 및 포커스 이슈를 해결할 수 있음
 * - @issue https://github.com/tailwindlabs/headlessui/issues/621
 * - @issue https://github.com/tailwindlabs/headlessui/issues/825
 */
const ContextModal: React.FC<ModalProps> = ({
  isPeak = false,
  zIndex = MODAL_ZINDEX_MIN,
  item,
  onClose,
  children,
}) => {
  const focusTarget = useRef<HTMLDivElement>(null);

  // const isSideModal = item?.isSide || false;
  const isOverlayClickable =
    item?.isOverlayClickable ?? DEFAULT_MODAL_CONFIG.isOverlayClickable;

  return (
    <Transition.Root as={React.Fragment} appear show>
      <Dialog
        as="div"
        initialFocus={isPeak ? focusTarget : null}
        __demoMode={!isPeak}
        style={{ zIndex: zIndex - 1 }}
        className="fixed inset-0 overflow-y-auto"
        open
        static={!isPeak}
        onClose={() => {
          if (isOverlayClickable) onClose();
          // if (isPeak && !isSideModal) onClose();
        }}
      >
        {item?.isSide ? (
          <div className="min-h-screen text-right">
            <Transition.Child as={React.Fragment} {...MODAL_OVERLAY_TRANSITION}>
              <Dialog.Overlay className="fixed inset-0 bg-black bg-opacity-10 cursor-pointer" />
            </Transition.Child>
            {/* This element is to trick the browser into centering the modal contents. */}
            <span
              className="inline-block h-screen align-middle"
              aria-hidden="true"
            >
              &#8203;
            </span>
            <Transition.Child
              as={React.Fragment}
              {...SIDE_MODAL_CONTENT_TRANSITION}
            >
              <_SideModalLayer
                ref={focusTarget}
                zIndex={zIndex}
                size={item?.size}
                hasCloseButton={item?.hasCloseButton}
                hasDefaultPadding={item?.hasDefaultPadding}
                onClose={onClose}
              >
                {({ onClose }) =>
                  item?.el ? React.cloneElement(item.el, { onClose }) : children
                }
              </_SideModalLayer>
            </Transition.Child>
          </div>
        ) : (
          <div className="min-h-screen px-4 text-center">
            <Transition.Child as={React.Fragment} {...MODAL_OVERLAY_TRANSITION}>
              <Dialog.Overlay className="fixed inset-0 bg-black bg-opacity-10 cursor-pointer" />
            </Transition.Child>
            {/* This element is to trick the browser into centering the modal contents. */}
            <span
              className="inline-block h-screen align-middle"
              aria-hidden="true"
            >
              &#8203;
            </span>
            <Transition.Child as={React.Fragment} {...MODAL_CONTENT_TRANSITION}>
              <_ModalLayer
                ref={focusTarget}
                zIndex={zIndex}
                size={item?.size}
                hasCloseButton={item?.hasCloseButton}
                hasDefaultPadding={item?.hasDefaultPadding}
                onClose={onClose}
              >
                {({ onClose }) =>
                  item?.el ? React.cloneElement(item.el, { onClose }) : children
                }
              </_ModalLayer>
            </Transition.Child>
          </div>
        )}
      </Dialog>
    </Transition.Root>
  );
};

export default ContextModal;
